import { makeApi } from "@zodios/core";
import { z } from "zod";
import { Enums, Schemas } from ".";

// ==========================================
// 1. Authentication & Identity Endpoints
// ==========================================
const authEndpoints = makeApi([
  {
    method: "post",
    path: "/auth/identityWallet/verify",
    alias: "postAuthIdentityWalletVerify",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            address: z.string(),
            message: z.string(),
            signature: z.string(),
          })
          .partial(),
      },
    ],
    response: Schemas.postAuthIdentityWalletVerify,
  },
  {
    method: "post",
    path: "/auth/identityWallet/nonce",
    alias: "postAuthIdentityWalletNonce",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            address: Schemas.AddressSchema.default,
          })
          .partial(),
      },
    ],
    response: Schemas.AuthIdentityWalletNonce.default,
  },
  {
    method: "post",
    path: "/auth/refresh",
    alias: "postauthrefresh",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            refreshToken: z.string().optional(),
          })
          .partial()
          .optional(),
      },
    ],
    response: z.object({}).passthrough().partial(),
  },
]);

// ==========================================
// 2. Loans & Lending Endpoints
// ==========================================
const loanEndpoints = makeApi([
  {
    method: "get",
    path: "/loans",
    alias: "getLoans",
    requestFormat: "json",
    parameters: [
      { name: "take", type: "Query", schema: z.number().int().optional() },
      { name: "skip", type: "Query", schema: z.number().int().optional() },
    ],
    response: Schemas.Loans.default,
  },
  {
    method: "get",
    path: "/loans/latestEvents",
    alias: "getLoansLatestEvents",
    requestFormat: "json",
    parameters: [
      {
        name: "transaction_type",
        type: "Query",
        schema: Schemas.Loans.LoanSchema.shape.transferPaymentFineType,
      },
      { name: "fromDate", type: "Query", schema: z.string().optional() },
      { name: "toDate", type: "Query", schema: z.string().optional() },
      { name: "payStatus", type: "Query", schema: z.string().optional() },
      { name: "take", type: "Query", schema: z.number().optional() },
      { name: "skip", type: "Query", schema: z.number().optional() },
    ],
    response: Schemas.LoansLatestEvents.default,
  },
  {
    method: "get",
    path: "/loans/ltvHistory",
    alias: "getLoansLtvHistory",
    requestFormat: "json",
    parameters: [
      { name: "userLoanId", type: "Query", schema: z.string().optional() },
    ],
    response: Schemas.LoanLtvHistory.default,
  },
  {
    method: "post",
    path: "/loans/new/",
    alias: "postLoansNew",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/loans/overview",
    alias: "getLoansOverview",
    requestFormat: "json",
    response: Schemas.LoansOverview.default,
  },
  {
    method: "post",
    path: "/loans/pay/",
    alias: "postloanspay",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/loans/user/",
    alias: "getLoansUser",
    requestFormat: "json",
    response: Schemas.LoansUser.default,
  },
  {
    method: "get",
    path: "/loans/user/:id",
    alias: "getLoansUserSlug",
    requestFormat: "json",
    parameters: [{ name: "id", type: "Path", schema: z.string() }],
    response: Schemas.LoansUserSlug.default,
  },
  {
    method: "get",
    path: "/loans/info/:id",
    alias: "getLoansInfoSlug",
    requestFormat: "json",
    parameters: [{ name: "id", type: "Path", schema: z.string() }],
    response: Schemas.LoanInfo.default,
  },
]);

// ==========================================
// 3. Orders & Trade Endpoints
// ==========================================
const orderEndpoints = makeApi([
  {
    method: "post",
    path: "/orders/",
    alias: "postOrders",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Schemas.CreateOrder.CreateOrderBodySchema,
      },
    ],
    response: Schemas.CreateOrder.default,
  },
  {
    method: "get",
    path: "/orders/",
    alias: "getOrders",
    requestFormat: "json",
    parameters: [
      { name: "skip", type: "Query", schema: z.string().optional() },
      { name: "status", type: "Query", schema: z.string().optional() },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/orders/close",
    alias: "postOrdersClose",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            price: z.number(),
            orderId: z.string(),
          })
          .partial(),
      },
    ],
    response: z.object({ meta: Schemas.Shared.MetaSchema.default }).partial(),
  },
  {
    method: "put",
    path: "/orders/modify/:id",
    alias: "putOrdersModify",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: Schemas.OrderModify.default },
    ],
    response: z.any(),
  },
  {
    method: "get",
    path: "/orders/overview",
    alias: "getOrdersOverview",
    requestFormat: "json",
    response: Schemas.OrderOverview.default,
  },
  {
    method: "get",
    path: "/trade/turnover/deposit",
    alias: "getTradeTurnoverDeposit",
    requestFormat: "json",
    response: Schemas.TradeTurnoverDeposit.default,
  },
  {
    method: "get",
    path: "/trade/turnover/withdraw",
    alias: "getTradeTurnoverWithdraw",
    requestFormat: "json",
    response: Schemas.TradeTurnoverWithdraw.default,
  },
]);

// ==========================================
// 4. Users, Profits & Investments Endpoints
// ==========================================
const userEndpoints = makeApi([
  {
    method: "get",
    path: "/users-investments/activePoolDetails",
    alias: "getUsersInvestmentsActivePoolDetails",
    requestFormat: "json",
    response: Schemas.UsersInvestmentsActivePoolDetails.default,
  },
  {
    method: "get",
    path: "/users-investments/activePoolSummary",
    alias: "getUsersInvestmentsActivePoolSummary",
    requestFormat: "json",
    parameters: [
      { name: "Origin", type: "Header", schema: z.string().optional() },
    ],
    response: Schemas.UsersInvestmentsActivePoolSummary.default,
  },
  {
    method: "get",
    path: "/users-investments/principalReturnsDetails",
    alias: "getUsersInvestmentsPrincipalReturnsDetails",
    requestFormat: "json",
    response: Schemas.UsersInvestmentsPrincipalReturnsDetails.default,
  },
  {
    method: "get",
    path: "/users-investments/principalReturnsSummary",
    alias: "getUsersInvestmentsPrincipalReturnsSummary",
    requestFormat: "json",
    response: Schemas.UsersInvestmentsPrincipalReturnsSummary.default,
  },
  {
    method: "put",
    path: "/users/",
    alias: "putusers",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/users/calcPlans",
    alias: "getUsersCalcPlans",
    requestFormat: "json",
    response: z.object({}).partial(),
  },
  {
    method: "post",
    path: "/users/connectWallet",
    alias: "postUsersConnectWallet",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Schemas.UserConnectWallet.ConnectWalletBodySchema,
      },
    ],
    response: Schemas.UserConnectWallet.default,
  },
  {
    method: "get",
    path: "/users/me",
    alias: "getUsersMe",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Schemas.UserConnectWallet.ConnectWalletBodySchema,
      },
    ],
    response: Schemas.UserConnectWallet.default,
  },
  {
    method: "get",
    path: "/users/info",
    alias: "getUsersInfo",
    requestFormat: "json",
    parameters: [{ name: "uid", type: "Query", schema: z.string().optional() }],
    response: Schemas.UsersInfo.default,
  },
  {
    method: "get",
    path: "/users/investmentPlanTypeAccess",
    alias: "getUsersInvestmentPlanTypeAccess",
    requestFormat: "json",
    parameters: [
      { name: "Origin", type: "Header", schema: z.string().optional() },
    ],
    response: Schemas.UsersInvestmentPlanTypeAccess.default,
  },
  {
    method: "get",
    path: "/users/marketingPlanHistory",
    alias: "getUsersMarketingPlanHistory",
    requestFormat: "json",
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/users/profits",
    alias: "getUsersProfits",
    requestFormat: "json",
    response: Schemas.UsersProfits.default,
  },
  {
    method: "get",
    path: "/users/referralDetails",
    alias: "getUsersReferralDetails",
    requestFormat: "json",
    response: Schemas.UserTree.UserReferralDetailsResponse,
  },
  {
    method: "get",
    path: "/users/referralInvestHistories",
    alias: "getUsersReferralInvestHistories",
    requestFormat: "json",
    parameters: [
      { name: "take", type: "Query", schema: z.number().int().optional() },
      { name: "skip", type: "Query", schema: z.number().int().optional() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/users/rewards",
    alias: "getUsersRewards",
    requestFormat: "json",
    parameters: [
      { name: "fromUID", type: "Query", schema: z.string().optional() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "post",
    path: "/users/setPositionInTree",
    alias: "postUsersSetPositionInTree",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/users/subsetsAtLevel/",
    alias: "getuserssubsetsAtLevel",
    requestFormat: "json",
    parameters: [
      { name: "level", type: "Query", schema: z.number().int().optional() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/users/subsetsAtLevel",
    alias: "getUsersSubsetsAtLevel",
    requestFormat: "json",
    parameters: [
      { name: "level", type: "Query", schema: z.number().int().optional() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/users/transferHistory",
    alias: "getUsersTransferHistory",
    requestFormat: "json",
    parameters: [
      { name: "take", type: "Query", schema: z.number().int().optional() },
      { name: "skip", type: "Query", schema: z.number().int().optional() },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/users/withdrawProfit",
    alias: "getUsersWithdrawProfit",
    requestFormat: "json",
    parameters: [
      { name: "tracerKey", type: "Query", schema: z.number().int().optional() },
      { name: "amount", type: "Query", schema: z.number().int().optional() },
      { name: "hash", type: "Query", schema: z.string().optional() },
      { name: "type", type: "Query", schema: z.string().optional() },
      { name: "coin", type: "Query", schema: z.string().optional() },
    ],
    response: Schemas.UsersWithdrawProfit.default,
  },
  {
    method: "get",
    path: "/users/withdrawProfitHistory",
    alias: "getUsersWithdrawProfitHistory",
    requestFormat: "json",
    parameters: [
      { name: "groupBy", type: "Query", schema: z.string().optional() },
      { name: "overView", type: "Query", schema: z.boolean().optional() },
    ],
    response: Schemas.UsersWithdrawProfit.default,
  },
]);

// ==========================================
// 5. Wallets & Transactions Endpoints
// ==========================================
const walletEndpoints = makeApi([
  {
    method: "get",
    path: "/wallets/addProfitToWithdrawable",
    alias: "getwalletsaddProfitToWithdrawable",
    requestFormat: "json",
    parameters: [
      { name: "userPlanId", type: "Query", schema: z.string().optional() },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/wallets/cacheTransaction",
    alias: "postWalletsCacheTransaction",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Schemas.WalletsCacheTransaction.CacheTransactionBodySchema,
      },
    ],
    response: Schemas.WalletsCacheTransaction.default,
  },
  {
    method: "post",
    path: "/wallets/convert",
    alias: "postWalletsConvert",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            amount: z.number(),
            amountConverted: z.number(),
            conversionType: Enums.Convert.default,
          })
          .partial(),
      },
    ],
    response: z.object({ meta: Schemas.Shared.MetaSchema.default }),
  },
  {
    method: "get",
    path: "/wallets/converts",
    alias: "getWalletsConverts",
    requestFormat: "json",
    parameters: [
      { name: "take", type: "Query", schema: z.number().int().optional() },
      { name: "skip", type: "Query", schema: z.number().int().optional() },
      { name: "fromDate", type: "Query", schema: z.string().optional() },
      { name: "toDate", type: "Query", schema: z.string().optional() },
      {
        name: "conversionType",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/wallets/deposit",
    alias: "postWalletsDeposit",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Schemas.WalletsDeposit.WalletDepositBodySchema,
      },
    ],
    response: Schemas.WalletsDeposit.default,
  },
  {
    method: "get",
    path: "/wallets/moveProfitWithdrawableToInvest",
    alias: "getwalletsmoveProfitWithdrawableToInvest",
    requestFormat: "json",
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/wallets/signMessage",
    alias: "getWalletsSignMessage",
    requestFormat: "json",
    parameters: [
      { name: "amount", type: "Query", schema: z.number().optional() },
      { name: "type", type: "Query", schema: z.string().optional() },
    ],
    response: Schemas.WalletsExtra.WalletsSignMessageResponse,
  },
  {
    method: "get",
    path: "/wallets/tracerKeyInfo",
    alias: "getWalletsTracerKeyInfo",
    requestFormat: "json",
    response: Schemas.WalletsExtra.WalletsTracerKeyInfoResponse,
  },
  {
    method: "get",
    path: "/wallets/transactions",
    alias: "getWalletsTransactions",
    requestFormat: "json",
    parameters: [
      { name: "take", type: "Query", schema: z.number().int().optional() },
      { name: "skip", type: "Query", schema: z.number().int().optional() },
      { name: "groupBy", type: "Query", schema: z.string().optional() },
    ],
    response: Schemas.WalletsTransactions.default,
  },
  {
    method: "get",
    path: "/wallets/transactions/latestEvents",
    alias: "getWalletsTransactionsLatestEvents",
    requestFormat: "json",
    response: Schemas.WalletsTransactionsLatestEvents.default,
  },
  {
    method: "post",
    path: "/wallets/transfer",
    alias: "postWalletsTransfer",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            receiverUUID: z.string(),
            amount: z.number(),
            type: z.string(),
          })
          .partial(),
      },
    ],
    response: Schemas.WalletsTransfer.default,
  },
  {
    method: "post",
    path: "/wallets/transferGGold",
    alias: "postWalletsTransferGGold",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({}).partial(),
      },
    ],
    response: z.any(),
  },
]);

// ==========================================
// 6. Webhooks (Blockchain / ETH)
// ==========================================
const webhookEndpoints = makeApi([
  {
    method: "post",
    path: "/webhooks/eth/arbitrage/deposit",
    alias: "postwebhooksetharbitragedeposit",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/webhooks/eth/arbitrage/withdraw",
    alias: "postwebhooksetharbitragewithdraw",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/webhooks/eth/loan/deposit",
    alias: "postwebhooksethloandeposit",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/webhooks/eth/trade/deposit",
    alias: "postwebhooksethtradedeposit",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
]);

// ==========================================
// 7. General, Notifications, Tickets & Platform
// ==========================================
const generalEndpoints = makeApi([
  // {
  //   method: "get",
  //   path: "/api/contract/test",
  //   alias: "getApicontracttest",
  //   requestFormat: "json",
  //   response: z.void(),
  // },
  {
    method: "get",
    path: "/campaign/",
    alias: "getCampaign",
    requestFormat: "json",
    response: Schemas.Campaign.default,
  },
  {
    method: "get",
    path: "/campaign/campaignUserSummary",
    alias: "getCampaignUserSummary",
    requestFormat: "json",
    response: Schemas.CampaignUserSummary.default,
  },
  {
    method: "get",
    path: "/notifications",
    alias: "getNotifications",
    requestFormat: "json",
    parameters: [
      { name: "isRead", type: "Query", schema: z.boolean().optional() },
      {
        name: "platform",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: Schemas.Notifications.default,
  },
  {
    method: "patch",
    path: "/notifications/read",
    alias: "patchNotificationsRead",
    requestFormat: "json",
    parameters: [{ name: "id", type: "Query", schema: z.string().optional() }],
    response: z.any(),
  },
  {
    method: "patch",
    path: "/notifications/read/:id",
    alias: "patchNotificationsReadById",
    requestFormat: "json",
    parameters: [{ name: "id", type: "Path", schema: z.string() }],
    response: z.any(),
  },
  {
    method: "get",
    path: "/platform",
    alias: "getPlatform",
    requestFormat: "json",
    response: Schemas.Platform.default,
  },
  {
    method: "get",
    path: "/platform/goldStream",
    alias: "getPlatformGoldStream",
    requestFormat: "json",
    parameters: [
      { name: "cycleId", type: "Query", schema: z.string().optional() },
    ],
    response: Schemas.GoldStreams.GoldStreamResponse,
  },
  {
    method: "get",
    path: "/platform/goldStreamHistory",
    alias: "getPlatformGoldStreamHistory",
    requestFormat: "json",
    response: Schemas.GoldStreams.GoldStreamHistoryResponse,
  },
  {
    method: "post",
    path: "/push-notification/send",
    alias: "postpushNotificationsend",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/push-notification/subscribe",
    alias: "postpushNotificationsubscribe",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/referralCodes/createInTree",
    alias: "postReferralCodesCreateInTree",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "post",
    path: "/referralCodes/fast-generate",
    alias: "postReferralCodesFastGenerate",
    requestFormat: "json",
    parameters: [
      { name: "body", type: "Body", schema: z.object({}).partial() },
    ],
    response: z.object({}).partial(),
  },
  {
    method: "get",
    path: "/referralCodes/findLeaderOfCode",
    alias: "getReferralCodesFindLeaderOfCode",
    requestFormat: "json",
    parameters: [
      { name: "code", type: "Query", schema: z.string().optional() },
    ],
    response: Schemas.ReferralCodesFindLeaderOfCode.default,
  },
  {
    method: "get",
    path: "/referralCodes/positional-codes",
    alias: "getReferralCodesPositionalCodes",
    requestFormat: "json",
    parameters: [
      { name: "fromDate", type: "Query", schema: z.string().optional() },
      { name: "toDate", type: "Query", schema: z.string().optional() },
    ],
    response: z.any(),
  },
  // {
  //   method: "get",
  //   path: "/tickets",
  //   alias: "gettickets",
  //   requestFormat: "json",
  //   response: z.object({}).partial(),
  // },
  // {
  //   method: "patch",
  //   path: "/tickets/close/2d5ffce4-0c98-4b7a-8e4b-c02711d2bc0a",
  //   alias: "patchticketsclose2d5ffce40c984b7a8e4bC02711d2bc0a",
  //   requestFormat: "json",
  //   response: z.object({}).partial(),
  // },
  // {
  //   method: "post",
  //   path: "/tickets/create",
  //   alias: "postticketscreate",
  //   requestFormat: "json",
  //   parameters: [
  //     { name: "body", type: "Body", schema: z.object({}).partial() },
  //   ],
  //   response: z.object({}).partial(),
  // },
]);

// ==========================================
// 7. General, Notifications, Tickets & Platform
// ==========================================
const investmentCycle = makeApi([
  {
    method: "get",
    path: "/investment-cycles",
    alias: "getInvestmentCycles",
    parameters: [
      { name: "status", type: "Query", schema: z.array(z.string()) },
    ],
    response: Schemas.InvestmentCycle.default,
  },
  {
    method: "post",
    path: "/investment-cycles/:id/decision",
    alias: "postDecision",
    requestFormat: "json",
    parameters: [
      { name: "id", type: "Path", schema: z.string().optional() },
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            action: z.enum(["WITHDRAW", "RENEW"]),
          })
          .optional(),
      },
    ],
    response: z.any(),
  },
]);

// ==========================================
// Build the Main API Instance
// ==========================================
const endpoints = makeApi([
  ...authEndpoints,
  ...loanEndpoints,
  ...orderEndpoints,
  ...userEndpoints,
  ...walletEndpoints,
  ...webhookEndpoints,
  ...generalEndpoints,
  ...investmentCycle,
]);

export default endpoints;
